import { api } from '@/lib/api';
import { DEFAULT_DESIGN_THEME, type PublicBranding } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

const DEFAULT_BRANDING: PublicBranding = {
  serviceName: 'innermate',
  logoUrl: '/logo.png',
  faviconUrl: '/favicon.png',
  designTheme: DEFAULT_DESIGN_THEME,
  chatFooterText: '',
};

/**
 * 사용자 화면(채팅·로그인·온보딩) 좌측 상단의 회사 브랜드명/로고를 반환.
 * 어드민 설정 후 30초 안에 자동 반영. 즉시 반영이 필요하면
 * useQueryClient().invalidateQueries({ queryKey: ['branding'] }).
 *
 * 관리자 화면(/admin)·위키 사이드바('위키') 는 이 훅을 사용하지 않는다.
 */
export function useBranding(): PublicBranding {
  const query = useQuery<PublicBranding>({
    queryKey: ['branding'],
    queryFn: () => api.branding(),
    staleTime: 30_000,
  });
  return query.data ?? DEFAULT_BRANDING;
}

/**
 * 브라우저 탭 title 을 serviceName 과 동기화.
 * 모든 화면(어드민·위키 포함) 공통으로 적용 — index.html 의 정적 <title> 은 첫 페인트용 폴백.
 * 루트 라우트에 한 번만 마운트하면 된다.
 */
export function BrowserTitleSync() {
  const { serviceName } = useBranding();
  useEffect(() => {
    document.title = serviceName;
  }, [serviceName]);
  return null;
}

/**
 * 브라우저 탭 favicon 을 어드민 설정한 커스텀 파비콘으로 동기화.
 * index.html 의 정적 `<link rel="icon" href="/favicon.png">` 은 첫 페인트용 폴백 —
 * 마운트 후 useBranding 응답으로 href 를 교체한다.
 * 다른 type/rel 의 icon 태그가 있어도 첫 번째 하나만 갱신 (Apple touch icon 등은 별도 처리).
 */
export function BrowserFaviconSync() {
  const { faviconUrl } = useBranding();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      // index.html 에 정적으로 박혀있어 거의 항상 존재하지만, 누락 케이스 방어.
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    if (link.href !== faviconUrl) {
      link.href = faviconUrl;
    }
  }, [faviconUrl]);
  return null;
}
